/**
 * The when-clause editor: a text field and a structured tree over one value.
 *
 * Both halves edit the same clause and stay in step. That is worth the cost
 * because the two ways of working are genuinely different — pasting
 * `terminalFocus && !diffOpen` from a config file is one gesture in the text
 * field and six in the tree, while discovering that `Not` and `or` exist at all
 * only happens in the tree.
 *
 * The text field owns a *draft* string rather than deriving from the AST. An
 * expression is unparseable for as long as it is half-typed, and a field that
 * reverts on every keystroke that does not parse cannot be typed into.
 *
 * @module
 */
import type { KeybindingWhenNode } from "@cmmd-forge/contracts";
import { PlusIcon, TriangleAlertIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  defaultWhenCondition,
  parseWhenExpressionDraft,
  unknownWhenVariables,
  whenAstToExpression,
} from "./keybindingWhenDraft";
import { WhenExpressionNodeEditor } from "./WhenExpressionNodeEditor";

export interface WhenExpressionBuilderProps {
  readonly value: KeybindingWhenNode | undefined;
  readonly variables: ReadonlyArray<string>;
  readonly onChange: (value: KeybindingWhenNode | undefined) => void;
  /** Lets the row disable Save while the text field holds something unparseable. */
  readonly onValidityChange?: ((valid: boolean) => void) | undefined;
}

export function WhenExpressionBuilder({
  value,
  variables,
  onChange,
  onValidityChange,
}: WhenExpressionBuilderProps) {
  const [draft, setDraft] = useState(() => whenAstToExpression(value));
  const parsed = useMemo(() => parseWhenExpressionDraft(draft), [draft]);
  const unknown = parsed.ok ? unknownWhenVariables(parsed.value, variables) : [];

  /** Typed into the field: publish only when it parses, but always keep the text. */
  const editText = (next: string) => {
    setDraft(next);
    const result = parseWhenExpressionDraft(next);
    onValidityChange?.(result.ok);
    if (result.ok) onChange(result.value);
  };

  /** Changed in the tree: the AST is authoritative, so the text follows it. */
  const editTree = (next: KeybindingWhenNode | undefined) => {
    setDraft(whenAstToExpression(next));
    onValidityChange?.(true);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Input
          value={draft}
          onChange={(event) => editText(event.currentTarget.value)}
          placeholder="Always"
          aria-label="When expression"
          aria-invalid={!parsed.ok}
          size="sm"
          className="font-mono"
        />
        {parsed.ok ? null : (
          <p role="alert" className="text-destructive-foreground text-xs">
            {parsed.message}
          </p>
        )}
        {unknown.length > 0 ? (
          <p className="flex items-center gap-1.5 text-warning-foreground text-xs">
            <TriangleAlertIcon className="size-3.5 shrink-0" />
            {/* A warning, not an error. The clause is valid and will save; it
                just never matches, which is a thing worth knowing and not a
                thing worth blocking — the context may exist again tomorrow. */}
            {unknown.join(", ")} {unknown.length === 1 ? "is" : "are"} not a context Forge sets
          </p>
        ) : null}
      </div>

      {value === undefined ? (
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground text-xs">Runs in every context.</p>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="ml-auto"
            onClick={() => editTree(defaultWhenCondition())}
          >
            <PlusIcon className="size-3.5" />
            Condition
          </Button>
        </div>
      ) : (
        <>
          <WhenExpressionNodeEditor node={value} variables={variables} onChange={editTree} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="text-muted-foreground"
                  onClick={() => editTree(undefined)}
                >
                  Clear condition
                </Button>
              }
            />
            <TooltipPopup side="top">The binding runs in every context</TooltipPopup>
          </Tooltip>
        </>
      )}
    </div>
  );
}
